/**
 * Goals Management Page - master-detail layout: client list -> Domain/Program/Goal-Target tree
 */

import { useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import type { Client } from '../../types/clients';
import { ClientListPanel } from './ClientListPanel';
import { ClientGoalsPanel, EmptyPanel } from './ClientGoalsPanel';
import { media } from '../../styles/media';

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// ---- Master-detail layout ----

const MasterDetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 360px) 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
  align-items: stretch;
  min-height: 420px;

  ${media.desktop} {
    grid-template-columns: 1fr;
  }
`;

const ListPanelWrapper = styled.div<{ $hasSelection: boolean }>`
  min-width: 0;

  ${media.desktop} {
    display: ${({ $hasSelection }) => ($hasSelection ? 'none' : 'block')};
  }
`;

const DetailPanelWrapper = styled.div<{ $hasSelection: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  min-width: 0;

  ${media.desktop} {
    display: ${({ $hasSelection }) => ($hasSelection ? 'flex' : 'none')};
  }
`;

const BackToClientsButton = styled.button`
  display: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  align-self: flex-start;
  border: none;
  background: none;
  padding: ${({ theme }) => theme.spacing[1]} 0;
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  min-height: 44px;

  svg {
    width: 16px;
    height: 16px;
  }

  ${media.desktop} {
    display: flex;
  }
`;

const DetailTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

// ==================== Component ====================

export const GoalsManagementPage = () => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  return (
    <Container>
      <Header>
        <Title>Goals</Title>
      </Header>

      <MasterDetailGrid>
        <ListPanelWrapper $hasSelection={!!selectedClient}>
          <ClientListPanel selectedClientId={selectedClient?.id ?? null} onSelect={setSelectedClient} />
        </ListPanelWrapper>

        <DetailPanelWrapper $hasSelection={!!selectedClient}>
          <BackToClientsButton type="button" onClick={() => setSelectedClient(null)}>
            <ArrowLeft aria-hidden="true" /> Back to clients
          </BackToClientsButton>

          {!selectedClient ? (
            <EmptyPanel
              title="Select a client to view their goals"
              text="Choose a client from the list to view or manage their Domain → Program → Goal/Target tree."
            />
          ) : (
            <>
              <DetailTitle>
                {selectedClient.firstName} {selectedClient.lastName}
              </DetailTitle>
              <ClientGoalsPanel
                clientId={selectedClient.id}
                clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                canManage
              />
            </>
          )}
        </DetailPanelWrapper>
      </MasterDetailGrid>
    </Container>
  );
};

export default GoalsManagementPage;
